import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import PrimaryCompetenciesList from './PrimaryCompetenciesList';
import CompetencyRadar from './CompetencyRadar';
import AllKeywordsSheet from './AllKeywordsSheet';
import CollaboratorsList from './CollaboratorsList';
import ViewToggleButton from './ViewToggleButton';
import type { Collaborator, CompetencyResult } from '../../types/api';

const PRIMARY_THRESHOLD = 8;
const MIN_OUTCOMES_FOR_RADAR = 6;

type CompetencyView = 'list' | 'radar';

interface CompetencyRowProps {
  competencies: (CompetencyResult | string)[];
  outcomesCount: number;
  projectsCount: number;
  onKeywordClick: (keyword: string, e: React.MouseEvent) => void;
  collaborators: Collaborator[];
  collaboratorsLoading: boolean;
  collaboratorsError: string | null;
  onCollaboratorClick: (personId: number, e: React.MouseEvent) => void;
  onCollaboratorProjectClick: (projectId: number, e: React.MouseEvent) => void;
  onShowGraph: () => void;
  graphLoading: boolean;
}

export default function CompetencyRow({
  competencies,
  outcomesCount,
  projectsCount,
  onKeywordClick,
  collaborators,
  collaboratorsLoading,
  collaboratorsError,
  onCollaboratorClick,
  onCollaboratorProjectClick,
  onShowGraph,
  graphLoading,
}: CompetencyRowProps) {
  const { t } = useTranslation('person');
  const [sheetOpen, setSheetOpen] = useState(false);

  const radarEnabled = outcomesCount >= MIN_OUTCOMES_FOR_RADAR;
  const [userPicked, setUserPicked] = useState<CompetencyView | null>(null);
  const activeView: CompetencyView = radarEnabled ? userPicked ?? 'radar' : 'list';

  const primariesCount = competencies.filter(
    (c): c is CompetencyResult => typeof c === 'object' && !!c.is_primary,
  ).length;
  const totalKeywords = competencies.length;
  const hasMore = totalKeywords > primariesCount;

  const isSelected = outcomesCount + projectsCount < PRIMARY_THRESHOLD;
  const sectionLabel = isSelected
    ? t('sections.selectedCompetencies')
    : t('sections.primaryCompetencies');
  const sectionTooltip = isSelected
    ? t('sections.selectedCompetenciesTooltip')
    : t('sections.primaryCompetenciesTooltip');

  return (
    <Card className="mb-6 border-ctu-border shadow-none">
      <CardContent className="grid grid-cols-1 gap-0 p-0 lg:grid-cols-2 lg:divide-x">
        <div className="p-6 lg:p-8">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-baseline gap-2">
              <TooltipProvider delayDuration={150}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span
                      className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ctu-text-muted cursor-help underline decoration-dotted decoration-ctu-text-muted/40 underline-offset-4"
                      tabIndex={0}
                    >
                      {sectionLabel}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" align="start" className="max-w-xs">
                    {sectionTooltip}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <span className="text-[11px] font-mono tabular-nums text-ctu-text-muted">
                {primariesCount}
              </span>
            </div>

            <div className="flex items-center gap-1">
              <TooltipProvider delayDuration={150}>
                <Tooltip>
                  {/* span wrapper: tooltip events don't fire on disabled buttons (Radix limitation) */}
                  <TooltipTrigger asChild>
                    <span>
                      <ViewToggleButton
                        active={activeView === 'radar'}
                        disabled={!radarEnabled}
                        onClick={() => setUserPicked('radar')}
                      >
                        {t('buttons.viewRadar')}
                      </ViewToggleButton>
                    </span>
                  </TooltipTrigger>
                  {!radarEnabled && (
                    <TooltipContent side="bottom" align="end" className="max-w-xs">
                      {t('competencyProfile.tooFewOutcomesDescription')}
                    </TooltipContent>
                  )}
                </Tooltip>
              </TooltipProvider>
              <span className="text-ctu-text-muted/40 text-xs select-none">/</span>
              <ViewToggleButton
                active={activeView === 'list'}
                onClick={() => setUserPicked('list')}
              >
                {t('buttons.viewList')}
              </ViewToggleButton>

              {hasMore && (
                <>
                  <span className="mx-2 h-4 w-px bg-border" aria-hidden />
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2 text-xs font-medium text-ctu-text-muted hover:text-ctu-text"
                    onClick={() => setSheetOpen(true)}
                  >
                    {t('buttons.viewAllKeywords')} ›
                  </Button>
                </>
              )}
            </div>
          </div>

          {activeView === 'list' ? (
            <PrimaryCompetenciesList
              competencies={competencies}
              onKeywordClick={onKeywordClick}
              emptyLabel={t('competenciesSection.noCompetencies')}
            />
          ) : (
            <CompetencyRadar competencies={competencies} />
          )}
        </div>
        <div className="border-t lg:border-t-0 p-6 lg:p-8">
          <CollaboratorsList
            collaborators={collaborators}
            loading={collaboratorsLoading}
            error={collaboratorsError}
            onPersonClick={onCollaboratorClick}
            onProjectClick={onCollaboratorProjectClick}
            onShowGraph={onShowGraph}
            graphLoading={graphLoading}
          />
        </div>
      </CardContent>

      <AllKeywordsSheet
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        competencies={competencies}
        outcomesCount={outcomesCount}
        onKeywordClick={(keyword, e) => {
          onKeywordClick(keyword, e);
          setSheetOpen(false);
        }}
      />
    </Card>
  );
}
